import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import { FixBugDialog, FIX_BUG_WORKFLOW_INSTRUCTIONS } from '../components/FixBugDialog';
import type { AppItem, Conversation, JobRow, ModelConfig, WorkerHealthResponse, ForceReleaseResponse } from '../types';
import { reportError, reportErrorLocal } from '../utils/errors';
import { getJobsFeedback } from './JobsPage.feedback';
import './JobsPage.css';

type AppsResponse = AppItem[] | { apps?: AppItem[] | null };

function normalizeAppsResponse(data: AppsResponse): AppItem[] {
  return Array.isArray(data) ? data : data.apps ?? [];
}

export default function JobsPage({
  apiClient,
  setError
}: {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
}) {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [apps, setApps] = useState<AppItem[]>([]);
  const [appId, setAppId] = useState('');
  const [limit, setLimit] = useState(200);
  const [loading, setLoading] = useState(true);
  const [jobsLoadFailed, setJobsLoadFailed] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [workerHealth, setWorkerHealth] = useState<WorkerHealthResponse | null>(null);
  const [releasingConversations, setReleasingConversations] = useState<Set<string>>(new Set());
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [fixBugOpen, setFixBugOpen] = useState(false);
  const [fixBugJob, setFixBugJob] = useState<JobRow | null>(null);
  const navigate = useNavigate();

  const formatTimestamp = (value: string | null | undefined) =>
    value ? new Date(value).toLocaleString() : '-';

  const isJobDelayed = (job: JobRow): boolean => {
    const runAt = new Date(job.run_at);
    return runAt.getTime() > Date.now();
  };

  const getDelayText = (job: JobRow): string => {
    if (job.status !== 'queued') return '-';
    const runAt = new Date(job.run_at);
    if (runAt.getTime() <= Date.now()) return '-';

    const diffMs = runAt.getTime() - Date.now();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 300) return 'Delayed (1yr+)';
    if (diffDays > 0) return `Delayed (${diffDays}d)`;
    if (diffHours > 0) return `Delayed (${diffHours}h)`;
    return 'Delayed';
  };

  useEffect(() => {
    apiClient
      .get<AppsResponse>('/api/apps')
      .then((data) => setApps(normalizeAppsResponse(data)))
      .catch((err) => reportErrorLocal('Failed to load apps', err, setError, setJobsError));
    apiClient
      .get<ModelConfig[]>('/api/me/models')
      .then((data) => setModels(data))
      .catch((err) => console.error('Failed to load models', err));
  }, [apiClient, setError]);

  const loadJobs = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (appId) {
      params.set('app_id', appId);
    }
    params.set('limit', String(limit));
    const query = params.toString();
    apiClient
      .get<JobRow[]>(`/api/jobs${query ? `?${query}` : ''}`)
      .then((data) => {
        setJobs(data ?? []);
        setJobsLoadFailed(false);
        setJobsError(null);
      })
      .catch((err) => {
        setJobsLoadFailed(true);
        reportErrorLocal('Failed to load jobs', err, setError, setJobsError);
      })
      .finally(() => setLoading(false));
  }, [apiClient, appId, limit, setError]);

  const loadWorkerHealth = useCallback(() => {
    apiClient
      .get<WorkerHealthResponse>('/api/jobs/worker-health')
      .then((data) => setWorkerHealth(data))
      .catch((err) => console.error('Failed to load worker health', err));
  }, [apiClient]);

  const handleForceRelease = async (conversationId: string) => {
    if (!confirm(`Force release conversation ${conversationId}? This will attempt to kill remote processes and reset any stuck jobs.`)) {
      return;
    }
    setReleasingConversations(prev => new Set(prev).add(conversationId));
    try {
      const response = await apiClient.post<ForceReleaseResponse>('/api/jobs/force-release', {
        conversation_id: conversationId
      });
      if (response.success) {
        let message = response.message;
        if (response.kill_errors && response.kill_errors.length > 0) {
          message += `\n\nKill errors:\n${response.kill_errors.join('\n')}`;
        }
        alert(`Released: ${message}`);
        loadJobs();
        loadWorkerHealth();
      } else {
        alert('Release failed');
      }
    } catch (err) {
      reportError('Failed to force release', err, setError);
    } finally {
      setReleasingConversations(prev => {
        const next = new Set(prev);
        next.delete(conversationId);
        return next;
      });
    }
  };

  useEffect(() => {
    loadJobs();
    loadWorkerHealth();
    const interval = setInterval(() => {
      loadWorkerHealth();
    }, 30000); // Refresh health every 30s
    return () => clearInterval(interval);
  }, [loadJobs, loadWorkerHealth]);

  const isStaleJob = (job: JobRow): boolean => {
    if (job.status !== 'processing') return false;
    if (!job.lock_at) return false;
    const lockAt = new Date(job.lock_at);
    return Date.now() - lockAt.getTime() > 10 * 60 * 1000; // 10 minutes
  };

  const jobsFeedback = getJobsFeedback(loading, jobsLoadFailed, jobs.length, Boolean(appId));

  return (
    <section className="stack jobs-page">
      {workerHealth && (
        <div className="card full">
          <div className="card-header">
            <h2>Executor Health</h2>
            <span className="hint">
              {workerHealth.active_workers.length} / {workerHealth.config.conversation_workers + workerHealth.config.background_workers} executors active
              {' '}({workerHealth.config.poll_interval_ms}ms poll)
              {workerHealth.stats.stale_running > 0 && (
                <span className="warning-text"> • {workerHealth.stats.stale_running} stale jobs</span>
              )}
            </span>
          </div>
          <div className="detail-grid">
            <div className="card">
              <h4>Active Executors</h4>
              {workerHealth.active_workers.length === 0 ? (
                <p className="warning-text">No active executors detected!</p>
              ) : (
                <ul className="worker-list">
                  {workerHealth.active_workers.map((worker) => (
                    <li key={worker.id} className="worker-item">
                      <span className="worker-id">{worker.id.slice(0, 8)}...</span>
                      <span className="worker-type">{worker.worker_type}</span>
                      <span className="worker-seen">{formatTimestamp(worker.last_seen)}</span>
                      {worker.current_job && (
                        <span className="worker-job">
                          → {worker.current_job.app_slug || 'unknown'}
                          <span className="job-id">({worker.current_job.job_id.slice(0, 8)}...)</span>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="card">
              <h4>Queue Stats</h4>
              <div className="stats-grid">
                <div className="stat">
                  <span className="stat-value">{workerHealth.stats.pending}</span>
                  <span className="stat-label">Pending</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{workerHealth.stats.running}</span>
                  <span className="stat-label">Running</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{workerHealth.stats.failed}</span>
                  <span className="stat-label">Failed</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{workerHealth.stats.done}</span>
                  <span className="stat-label">Done</span>
                </div>
                <div className="stat warning">
                  <span className="stat-value">{workerHealth.stats.stale_running}</span>
                  <span className="stat-label">Stale</span>
                </div>
                <div className="stat warning">
                  <span className="stat-value">{workerHealth.stats.delayed}</span>
                  <span className="stat-label">Delayed</span>
                </div>
              </div>
            </div>
            <div className="card">
              <h4>Configuration</h4>
              <div className="config-grid">
                <div className="config-item">
                  <span className="config-label">Conversation:</span>
                  <span className="config-value">{workerHealth.config.conversation_workers}</span>
                </div>
                <div className="config-item">
                  <span className="config-label">Background:</span>
                  <span className="config-value">{workerHealth.config.background_workers}</span>
                </div>
                <div className="config-item">
                  <span className="config-label">Poll interval:</span>
                  <span className="config-value">{workerHealth.config.poll_interval_ms}ms</span>
                </div>
              </div>
            </div>
            {workerHealth.model_gateway && (
              <div className="card">
                <h4>AI Fence Rentals</h4>
                {workerHealth.model_gateway.credential_pool_id && (
                  <p className="status">Pool: {workerHealth.model_gateway.credential_pool_id}</p>
                )}
                {workerHealth.model_gateway.error ? (
                  <p className="warning-text">{workerHealth.model_gateway.error}</p>
                ) : workerHealth.model_gateway.active_rentals.length === 0 ? (
                  <p className="status">No rented subscription credentials.</p>
                ) : (
                  <ul className="worker-list">
                    {workerHealth.model_gateway.active_rentals.map((rental) => (
                      <li key={`${rental.pool_id}-${rental.key_id}`} className="worker-item">
                        <span className="worker-seen">{formatTimestamp(rental.rented_at)}</span>
                        <span>{rental.reason}</span>
                        <span className="worker-seen">{rental.pool_id}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card full">
        <div className="card-header">
          <h2>Job Monitor</h2>
          <div className="row">
            <button onClick={loadJobs} disabled={loading}>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>
        {jobsError && (
          <div role="alert">
            <ErrorBanner message={jobsError} onDismiss={() => setJobsError(null)} />
          </div>
        )}
        <div className="detail-grid">
          <label className="stack">
            App
            <select value={appId} onChange={(event) => setAppId(event.target.value)}>
              <option value="">All apps</option>
              {apps.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </label>
          <label className="stack">
            Limit
            <input
              type="number"
              min={1}
              max={500}
              value={limit}
              onChange={(event) => setLimit(Number(event.target.value))}
            />
          </label>
          <div className="row">
            <button onClick={loadJobs} disabled={loading}>
              Apply filters
            </button>
          </div>
        </div>
      </div>

      <div className="card full">
        <div className="card-header">
          <h3 id="jobs-results-heading">Recent jobs</h3>
        </div>
        <p className="hint" role="status" aria-live="polite" aria-atomic="true">
          {jobsFeedback}
        </p>
        <p className="hint" id="jobs-scroll-help">
          Scroll horizontally to see all columns. With the table area focused, use the arrow keys.
        </p>
        <div
          className="table-scroll jobs-page__table-scroll"
          role="region"
          aria-labelledby="jobs-results-heading"
          aria-describedby="jobs-scroll-help"
          aria-busy={loading}
          tabIndex={0}
        >
          <table aria-labelledby="jobs-results-heading">
            <thead>
              <tr>
                <th scope="col">Time</th>
                <th scope="col">Status</th>
                <th scope="col">Delay</th>
                <th scope="col">App</th>
                <th scope="col">Job</th>
                <th scope="col">Worker</th>
                <th scope="col">Attempts</th>
                <th scope="col">Error</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const appLabel = job.app_name || job.app_slug || job.app_id || '-';
                const jobLabel = job.id;
                const canShowLogs = Boolean(job.id);
                const canShowTrace = Boolean(job.trace_id);
                const delayed = isJobDelayed(job);
                const stale = isStaleJob(job);
                const canForceRelease = job.status === 'processing' && job.conversation_id;
                return (
                  <tr key={job.id} className={stale ? 'stale-job' : delayed ? 'delayed-job' : ''}>
                    <td>{formatTimestamp(job.updated_at)}</td>
                    <td>
                      {job.status}
                      {stale && <span className="stale-badge">STALE</span>}
                    </td>
                    <td>
                      {delayed ? (
                        <span className="delayed-badge">{getDelayText(job)}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="mono">{appLabel}</td>
                    <td className="mono">{jobLabel}</td>
                    <td className="mono">{job.lock_by ?? '-'}</td>
                    <td>{job.attempt_count}</td>
                    <td>{job.error ?? '-'}</td>
                    <td>
                      <div className="row jobs-page__actions">
                        {canShowLogs ? (
                          <Link
                            className="link-button"
                            aria-label={`Show logs for job ${job.id}`}
                            to={`/logs?span_id=${encodeURIComponent(job.id)}`}
                          >
                            Show logs
                          </Link>
                        ) : (
                          <button className="secondary" disabled>
                            Show logs
                          </button>
                        )}
                        {canShowTrace && (
                          <Link
                            className="link-button secondary"
                            aria-label={`Trace for job ${job.id}`}
                            to={`/logs?trace_id=${encodeURIComponent(job.trace_id ?? '')}`}
                          >
                            Trace
                          </Link>
                        )}
                        {canShowLogs && (
                          <button
                            type="button"
                            className="link-button secondary"
                            aria-label={`Fix bug for job ${job.id}`}
                            onClick={() => {
                              setFixBugJob(job);
                              setFixBugOpen(true);
                            }}
                          >
                            Fix bug
                          </button>
                        )}
                        {canForceRelease && (
                          <button
                            className="warning"
                            aria-label={`${releasingConversations.has(job.conversation_id!) ? 'Releasing' : 'Force Release'} for job ${job.id}`}
                            onClick={() => handleForceRelease(job.conversation_id!)}
                            disabled={releasingConversations.has(job.conversation_id!)}
                          >
                            {releasingConversations.has(job.conversation_id!)
                              ? 'Releasing...'
                              : 'Force Release'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {jobs.length === 0 && (
                <tr>
                  <td colSpan={9}>{jobsFeedback}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FixBugDialog
        open={fixBugOpen}
        onClose={() => setFixBugOpen(false)}
        apiClient={apiClient}
        models={models}
        apps={apps}
        defaultAppId={apps.find((app) => app.is_self)?.id}
        defaultTitle="Fix Bug"
        defaultPrompt={`Please investigate and fix the issue with this job.

Job ID: ${fixBugJob?.id || 'unknown'}
${fixBugJob?.error ? `Error: ${fixBugJob.error}` : ''}

You can use the MCP tools to query logs:
- Use get_logs with job-related filters to investigate
- Use get_log_detail to get full details of specific log entries

Focus on understanding what went wrong from the error messages, relevant traces and spans, and the root cause.

${FIX_BUG_WORKFLOW_INSTRUCTIONS}`}
        onCreated={(created: Conversation) => navigate(`/conversations/${created.id}`)}
        setError={setError}
      />
    </section>
  );
}
